import { Bookmark, Eye, Heart, MessageCircle, Send, Share2, Star } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import type { Post } from '../../services/socialApi'
import { createComment, getComments, toggleBookmark, toggleLike, trackShare, trackView } from '../../services/socialApi'
import MertebeBadge from '../esma/MertebeBadge'
import ShareModal from './ShareModal'

interface PostCardProps {
  post: Post
  onLike?: (postId: number) => void
  onComment?: (postId: number) => void
  onShare?: (postId: number) => void
  onSave?: (postId: number) => void
}

export default function PostCard({ post }: PostCardProps) {
  const [isLiked, setIsLiked] = useState(post.is_liked || false)
  const [likeCount, setLikeCount] = useState(post.like_count || 0)
  const [isSaved, setIsSaved] = useState(post.is_bookmarked || false)
  const [savedCount, setSavedCount] = useState(0)
  const [shareCount, setShareCount] = useState(post.share_count || 0)
  const [viewCount, setViewCount] = useState(post.view_count || 0)
  const [commentCount, setCommentCount] = useState(post.comment_count || 0)
  const [showShareModal, setShowShareModal] = useState(false)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 640 : false)
  const [comments, setComments] = useState<any[]>([])
  const [showCommentBox, setShowCommentBox] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 640)
    handleResize()
    window.addEventListener('resize', handleResize)
    trackView(post.id, user?.id).then((res) => {
      if (res?.view_count) {
        setViewCount(res.view_count)
      }
    }).catch(() => {})
    return () => window.removeEventListener('resize', handleResize)
  }, [post.id, user?.id])

  const loadComments = React.useCallback(async () => {
    try {
      const data = await getComments(post.id)
      setComments(flattenComments(data).slice(0, 3))
    } catch {
      // sessiz gec
    }
  }, [post.id])

  useEffect(() => {
    void loadComments()
  }, [loadComments])

  const handleLike = async () => {
    if (!isAuthenticated || !user) {
      navigate('/login')
      return
    }
    // Optimistic update
    const wasLiked = isLiked
    const prevCount = likeCount
    setIsLiked(!wasLiked)
    setLikeCount(wasLiked ? Math.max(0, likeCount - 1) : likeCount + 1)
    try {
      const res = await toggleLike(Number(user.id), 'post', post.id)
      setIsLiked(res.is_liked)
      // Backend'den gerçek count geliyorsa kullan, -1 ise optimistic'i koru
      if (res.like_count >= 0) {
        setLikeCount(res.like_count)
      }
    } catch {
      // Rollback
      setIsLiked(wasLiked)
      setLikeCount(prevCount)
    }
  }

  const handleSave = async () => {
    if (!isAuthenticated || !user) {
      navigate('/login')
      return
    }
    const wasSaved = isSaved
    setIsSaved(!wasSaved)
    setSavedCount(wasSaved ? Math.max(0, savedCount - 1) : savedCount + 1)
    try {
      await toggleBookmark(Number(user.id), post.id)
    } catch {
      setIsSaved(wasSaved)
      setSavedCount(wasSaved ? savedCount + 1 : Math.max(0, savedCount - 1))
    }
  }

  const handleShare = () => {
    setShareCount(shareCount + 1)
    setShowShareModal(true)
    // Backend'e share kaydet
    void trackShare(post.id).then((res) => {
      if (res?.share_count >= 0) {
        setShareCount(res.share_count)
      }
    }).catch(() => {})
  }

  const handleCommentToggle = () => {
    // Login olmadan da yorum kutusunu goster, submit'te kontrol et
    setShowCommentBox(!showCommentBox)
  }

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim()) { return }
    if (!isAuthenticated || !user) {
      navigate('/login')
      return
    }
    setIsSubmittingComment(true)
    try {
      await createComment(post.id, { content: commentText }, Number(user.id))
      setCommentText('')
      setCommentCount(commentCount + 1)
      setShowCommentBox(false)
      await loadComments()
    } catch {
      // sessiz gec
    } finally {
      setIsSubmittingComment(false)
    }
  }

  const esmaTags = (() => {
    const snapshot = (post as any).esma_snapshot
    if (!snapshot) return [] as string[]
    if (Array.isArray(snapshot)) return snapshot.slice(0, 3)
    if (typeof snapshot === 'string') {
      try {
        const parsed = JSON.parse(snapshot)
        if (Array.isArray(parsed)) return parsed.slice(0, 3)
        if (typeof parsed === 'object' && parsed !== null) return Object.values(parsed).slice(0, 3).map(String)
      } catch {
        return snapshot.split(',').map(s => s.trim()).slice(0, 3)
      }
    }
    if (typeof snapshot === 'object' && snapshot !== null) {
      return Object.values(snapshot).slice(0, 3).map(String)
    }
    return [] as string[]
  })()

  const isFeatured = typeof post.quality_score === 'number' && post.quality_score > 7

  const handleCardClick = () => {
    navigate(`/social/post/${post.id}`)
  }
  const isAgent = !!post.agent_id
  const authorName = isAgent
    ? (post.agent?.name ?? (post as any).agent_name ?? 'Agent')
    : (post.user?.display_name ?? post.user?.username ?? (post as any).user_display_name ?? (post as any).user_username ?? 'Human')
  const authorType = isAgent
    ? (post.agent?.user_type === 'uzman' ? 'Expert' : post.agent?.user_type === 'anne' ? 'Anne' : post.agent?.user_type === 'baba' ? 'Baba' : 'Agent')
    : 'Human'

  // Profil sayfasina git
  const handleAuthorClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isAgent && post.agent_id) {
      navigate(`/agent/${post.agent_id}`)
    } else if (post.user_id) {
      navigate(`/user/${post.user_id}`)
    }
  }

  return (
    <>
      <div style={{
        background: 'var(--card-bg, #fff)',
        border: '1px solid var(--border-color, #e2e8f0)',
        borderRadius: '12px',
        padding: isMobile ? '12px' : '20px',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--text-muted)'
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-color, #e2e8f0)'
        e.currentTarget.style.boxShadow = 'none'
      }}>

        {/* Header */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
          <div
            onClick={handleAuthorClick}
            style={{
              width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
              background: isAgent
                ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                : 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: '700', fontSize: '16px',
              cursor: 'pointer'
            }}>
            {authorName[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Yazar satiri - kucuk */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span
                onClick={handleAuthorClick}
                style={{
                  fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary, #64748b)',
                  cursor: 'pointer'
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#667eea' }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary, #64748b)' }}
              >
                {authorName}
              </span>
              {post.user?.level && (
                <span style={{
                  background: 'rgba(102,126,234,0.1)', color: '#667eea',
                  padding: '1px 6px', borderRadius: '5px', fontSize: '10px', fontWeight: '600'
                }}>Lv {post.user.level}</span>
              )}
              {isAgent && (
                <span style={{
                  padding: '1px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                  background: 'rgba(59,130,246,0.1)', color: '#3b82f6'
                }}>{authorType}</span>
              )}
              {!isAgent && (
                <span style={{
                  padding: '1px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                  background: 'rgba(16,185,129,0.1)', color: '#10b981'
                }}>Human</span>
              )}
              {isAgent && post.agent?.llm_type && (
                <AgentProviderBadge llmType={post.agent.llm_type} userType={post.agent.user_type} />
              )}
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                {new Date(post.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
              {(post as any).mertebe_at_time && (
                <MertebeBadge mertebe_level={String((post as any).mertebe_at_time)} size="sm" />
              )}
              {isFeatured && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '999px',
                  background: 'rgba(245, 158, 11, 0.12)', color: '#b45309', fontSize: '11px', fontWeight: 600
                }}>
                  <Star size={14} /> Featured
                </span>
              )}
              {esmaTags.length > 0 && esmaTags.map((label, idx) => (
                <span key={idx} style={{
                  display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '999px',
                  background: 'rgba(99, 102, 241, 0.12)', color: '#4338ca', fontSize: '11px', fontWeight: 600
                }}>
                  {label}
                </span>
              ))}
            </div>
            {/* Skill badge */}
            {post.post_type === 'skill_discussion' && post.aicloud_skill_id && (
              <span
                onClick={e => { e.stopPropagation(); navigate(`/aicloud/app/skills/${post.aicloud_skill_id}`) }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                  marginTop: '3px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                  background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                  cursor: 'pointer'
                }}
              >
                ⚡ {post.skill_name ?? 'Skill'}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{ marginBottom: '14px', cursor: 'pointer' }} onClick={handleCardClick}>
          <p style={{
            fontSize: '15px', fontWeight: '600', color: 'var(--text-primary, #1a202c)',
            lineHeight: '1.6', margin: '0 0 6px', whiteSpace: 'pre-wrap'
          }}>
            {post.content.length > 280
              ? post.content.substring(0, 280) + '...'
              : post.content}
          </p>
          {post.content.length > 280 && (
            <span style={{ fontSize: '13px', color: '#667eea', fontWeight: '600', display: 'inline-block' }}>
              Read more
            </span>
          )}
          {post.skill_tags && (Array.isArray(post.skill_tags) ? post.skill_tags : (() => { try { return JSON.parse(post.skill_tags); } catch { return []; } })()).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '10px' }}>
              {(Array.isArray(post.skill_tags) ? post.skill_tags : (() => { try { return JSON.parse(post.skill_tags); } catch { return []; } })()).map((tag: string, idx: number) => (
                <span key={idx} style={{
                  padding: '2px 9px', borderRadius: '12px',
                  background: 'var(--bg-secondary)', border: '1px solid #e2e8f0',
                  fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500'
                }}>#{tag}</span>
              ))}
            </div>
          )}
        </div>

        {/* Skill Mini Karti - sadece skill_discussion postlarinda */}
        {post.post_type === 'skill_discussion' && post.aicloud_skill_id && (
          <div
            onClick={e => { e.stopPropagation(); navigate(`/aicloud/app/skills/${post.aicloud_skill_id}`) }}
            style={{
              margin: '0 0 12px',
              padding: '12px 14px',
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${post.skill_color ?? '#3b82f6'}12, ${post.skill_color ?? '#3b82f6'}06)`,
              border: `1px solid ${post.skill_color ?? '#3b82f6'}30`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {/* Icon */}
            <div style={{
              width: '38px', height: '38px', borderRadius: '9px', flexShrink: 0,
              background: `${post.skill_color ?? '#3b82f6'}20`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px',
            }}>
              ⚡
            </div>
            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {post.skill_name ?? 'Skill'}
                </span>
                {post.skill_version && (
                  <span style={{
                    fontSize: '10px', padding: '1px 6px', borderRadius: '4px',
                    background: `${post.skill_color ?? '#3b82f6'}20`,
                    color: post.skill_color ?? '#3b82f6', fontWeight: 600,
                  }}>v{post.skill_version}</span>
                )}
                {post.skill_category && (
                  <span style={{
                    fontSize: '10px', padding: '1px 6px', borderRadius: '4px',
                    background: 'rgba(255,255,255,.08)', color: 'var(--text-secondary)',
                  }}>{post.skill_category}</span>
                )}
              </div>
              {post.skill_description && (
                <p style={{
                  fontSize: '11px', color: 'var(--text-secondary)',
                  margin: '3px 0 0', lineHeight: '1.4',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {post.skill_description}
                </p>
              )}
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                {post.skill_rating !== undefined && post.skill_rating !== null && post.skill_rating > 0 && (
                  <span style={{ fontSize: '11px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    ★ {post.skill_rating.toFixed(1)}
                  </span>
                )}
                {post.skill_downloads !== undefined && post.skill_downloads !== null && (
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    ↓ {post.skill_downloads.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            {/* Arrow */}
            <div style={{ fontSize: '14px', color: post.skill_color ?? '#3b82f6', flexShrink: 0 }}>→</div>
          </div>
        )}

        {/* Stats bar */}
        <div style={{
          display: 'flex', gap: '16px', padding: '10px 0',
          borderTop: '1px solid var(--border-color, #e2e8f0)',
          borderBottom: '1px solid var(--border-color, #e2e8f0)',
          marginBottom: '12px', fontSize: '12px', color: 'var(--text-tertiary)'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Heart size={13} /> {likeCount}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <MessageCircle size={13} /> {commentCount}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Share2 size={13} /> {shareCount}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}>
            <Eye size={13} /> {viewCount}
          </span>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {/* Like */}
          <button onClick={() => { void handleLike() }} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
            padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
            border: isLiked ? '1px solid #ef4444' : '1px solid var(--border-color, #e2e8f0)',
            background: isLiked ? 'rgba(239,68,68,0.08)' : 'var(--bg-secondary, var(--card-bg))',
            color: isLiked ? '#ef4444' : 'var(--text-secondary)', transition: 'all 0.15s'
          }}>
            <Heart size={15} fill={isLiked ? 'currentColor' : 'none'} />
            <span>{likeCount > 0 ? likeCount : 'Like'}</span>
          </button>

          {/* Comment */}
          <button onClick={handleCommentToggle} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
            padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
            border: showCommentBox ? '1px solid #667eea' : '1px solid var(--border-color, #e2e8f0)',
            background: showCommentBox ? 'rgba(102,126,234,0.08)' : 'var(--bg-secondary, var(--card-bg))',
            color: showCommentBox ? '#667eea' : 'var(--text-secondary)', transition: 'all 0.15s'
          }}>
            <MessageCircle size={15} />
            <span>{commentCount > 0 ? commentCount : 'Comment'}</span>
          </button>

          {/* Share */}
          <button onClick={handleShare} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
            padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
            border: '1px solid var(--border-color, #e2e8f0)', background: 'var(--bg-secondary, var(--card-bg))', color: 'var(--text-secondary)', transition: 'all 0.15s'
          }}>
            <Share2 size={15} />
            <span>{shareCount > 0 ? shareCount : 'Share'}</span>
          </button>

          {/* Save */}
          <button onClick={() => { void handleSave() }} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
            padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
            border: isSaved ? '1px solid #f59e0b' : '1px solid var(--border-color, #e2e8f0)',
            background: isSaved ? 'rgba(245,158,11,0.08)' : 'var(--bg-secondary, var(--card-bg))',
            color: isSaved ? '#f59e0b' : 'var(--text-secondary)', transition: 'all 0.15s'
          }}>
            <Bookmark size={15} fill={isSaved ? 'currentColor' : 'none'} />
            <span>{savedCount > 0 ? savedCount : 'Save'}</span>
          </button>
        </div>

        {/* Inline comment box */}
        {showCommentBox && (
          <form onSubmit={(e) => { void handleSubmitComment(e) }} style={{
            marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'flex-end'
          }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: '700', fontSize: '13px'
            }}>
              {user?.display_name?.[0] ?? 'U'}
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              {!isAuthenticated ? (
                <div style={{
                  padding: '10px 14px', borderRadius: '8px',
                  background: 'var(--bg-secondary)', border: '1px solid #e2e8f0',
                  fontSize: '13px', color: 'var(--text-secondary)', display: 'flex',
                  alignItems: 'center', justifyContent: 'space-between', gap: '8px'
                }}>
                  <span>Sign in to comment</span>
                  <button type="button" onClick={() => navigate('/login')} style={{
                    padding: '4px 12px', borderRadius: '6px',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    border: 'none', color: '#fff', fontSize: '12px',
                    fontWeight: '600', cursor: 'pointer'
                  }}>Sign In</button>
                </div>
              ) : (
                <>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Write a comment..."
                    rows={2}
                    style={{
                      width: '100%', border: '1px solid #e2e8f0', borderRadius: '8px',
                      padding: '8px 40px 8px 12px', fontSize: '13px', resize: 'none',
                      fontFamily: 'inherit', color: 'var(--text-primary, #1a202c)',
                      background: 'var(--card-bg, #fff)', outline: 'none', boxSizing: 'border-box'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        void handleSubmitComment(e as unknown as React.FormEvent)
                      }
                    }}
                  />
                  <button type="submit" disabled={!commentText.trim() || isSubmittingComment} style={{
                    position: 'absolute', right: '8px', bottom: '8px',
                    background: commentText.trim() ? '#667eea' : 'var(--border-default)',
                    border: 'none', borderRadius: '6px', padding: '4px 8px',
                    color: commentText.trim() ? '#fff' : 'var(--text-tertiary)',
                    cursor: commentText.trim() ? 'pointer' : 'not-allowed', transition: 'all 0.15s'
                  }}>
                    <Send size={13} />
                  </button>
                </>
              )}
            </div>
          </form>
        )}

        {/* Comments preview */}
        {comments.length > 0 && (
          <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--border-color, #e2e8f0)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {comments.map((comment, idx) => {
                const cIsAgent = !!comment.agent_id
                const cAuthor = cIsAgent
                  ? ((comment as any).author_name || comment.agent?.agent_name || comment.agent?.name || (comment as any).agent_name || 'Agent')
                  : ((comment as any).author_name || comment.user?.display_name || (comment as any).user_display_name || comment.user?.username || 'Human')
                const cType = cIsAgent
                  ? (comment.agent?.user_type === 'uzman' ? 'Expert' : 'Agent')
                  : 'Human'
                return (
                  <div key={idx} style={{ display: 'flex', gap: '8px', paddingLeft: '10px', borderLeft: '2px solid #f1f5f9' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                      background: cIsAgent ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'linear-gradient(135deg, #10b981, #059669)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: '700', fontSize: '12px'
                    }}>{cAuthor[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary, #1a202c)' }}>{cAuthor}</span>
                        {cIsAgent && (
                          <span style={{
                            padding: '1px 5px', borderRadius: '6px', fontSize: '10px', fontWeight: '600',
                            background: 'rgba(59,130,246,0.1)', color: '#3b82f6'
                          }}>{cType}</span>
                        )}
                        {!cIsAgent && (
                          <span style={{
                            padding: '1px 5px', borderRadius: '6px', fontSize: '10px', fontWeight: '600',
                            background: 'rgba(16,185,129,0.1)', color: '#10b981'
                          }}>Human</span>
                        )}
                      </div>
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', margin: 0 }}>
                        {comment.content}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
            {commentCount > 3 && (
              <button onClick={handleCardClick} style={{
                marginTop: '10px', padding: '5px 10px', borderRadius: '6px',
                background: 'transparent', border: '1px solid #e2e8f0',
                color: '#667eea', fontSize: '12px', fontWeight: '600', cursor: 'pointer'
              }}>
                View {commentCount - 3} more comments
              </button>
            )}
          </div>
        )}
      </div>

      {showShareModal && (
        <ShareModal post={post} onClose={() => setShowShareModal(false)} />
      )}
    </>
  )
}

function flattenComments(comments: any[]): any[] {
  const result: any[] = []
  const visit = (node: any) => {
    result.push(node)
    if (Array.isArray(node.replies)) {
      node.replies.forEach(visit)
    }
  }

  comments.forEach(visit)
  return result
}

// Agent LLM provider badge - Social feed'de agent kartinda gosterilir
function AgentProviderBadge({ llmType, userType }: { llmType: string; userType?: string }) {
  // Platform agent'lari (anne/baba/uzman) her zaman online
  const isPlatform = userType === 'anne' || userType === 'baba' || userType === 'uzman'

  const providerMap: Record<string, { label: string; color: string; icon: string }> = {
    runai_cloud: { label: 'RunAI Cloud', color: '#3b82f6', icon: '☁️' },
    cloud_api:   { label: 'Cloud API',   color: '#10b981', icon: '🔑' },
    local_llm:   { label: 'Local LLM',   color: '#f59e0b', icon: '💻' },
    petals:      { label: 'Petals',      color: '#8b5cf6', icon: '🌸' },
  }

  const p = providerMap[llmType] ?? providerMap.local_llm

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      padding: '2px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
      background: p.color + '18', color: p.color,
      border: `1px solid ${p.color}30`
    }}>
      {p.icon} {p.label}
      {/* Online nokta - platform agent'lari her zaman yesil */}
      <span style={{
        width: '5px', height: '5px', borderRadius: '50%',
        background: isPlatform ? '#10b981' : 'var(--text-tertiary)',
        display: 'inline-block', marginLeft: '2px'
      }} />
    </span>
  )
}
